// src/admin/ManageGames.jsx
import { useState, useEffect } from "react";
import { getAllGames, addGame, updateGame, deactivateGame } from "../services/gameService";

const GENRES = ["Action","RPG","Adventure","Open World","Sports","Horror","Strategy","Simulation","Souls-like","Racing","Puzzle","Fighting"];

const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

/* ── Form ── */
.mg-form {
  background:#1a1a1a; border:1px solid rgba(0,120,242,0.2);
  border-radius:10px; padding:22px; margin-bottom:18px;
}
.mg-form-title { font-size:14px; font-weight:700; color:#fff; margin-bottom:16px; }

.mg-inp {
  width:100%; background:rgba(255,255,255,0.05);
  border:1px solid rgba(255,255,255,0.08); border-radius:6px;
  padding:10px 12px; color:#fff;
  font-family:'Inter',sans-serif; font-size:13px; outline:none;
  transition:border-color 0.15s;
}
.mg-inp:focus { border-color:rgba(0,120,242,0.4); }
.mg-lbl {
  display:block; font-size:10px; font-weight:700; color:#6b6b6b;
  letter-spacing:1px; text-transform:uppercase; margin-bottom:6px;
}

.mg-form-grid  { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-bottom:14px; }
.mg-form-grid3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:14px; margin-bottom:18px; }
.mg-price-row  { display:grid; grid-template-columns:1fr 1fr; gap:10px; }

.mg-genre-btn {
  padding:5px 12px; border-radius:4px; font-size:11px; font-weight:600;
  cursor:pointer; transition:all 0.12s; border:1px solid;
  background:none; font-family:'Inter',sans-serif;
}

.mg-btn-save {
  padding:11px 24px; background:#0078F2; border:none; border-radius:6px;
  color:#fff; font-family:'Inter',sans-serif; font-size:13px; font-weight:700;
  cursor:pointer; transition:all 0.15s;
}
.mg-btn-save:hover:not(:disabled) { background:#0a84ff; transform:translateY(-1px); }
.mg-btn-save:disabled { opacity:0.4; cursor:not-allowed; }
.mg-btn-cancel {
  padding:11px 18px; background:rgba(255,255,255,0.06);
  border:1px solid rgba(255,255,255,0.1); border-radius:6px;
  color:#a3a3a3; font-family:'Inter',sans-serif; font-size:13px; font-weight:500;
  cursor:pointer; transition:all 0.15s;
}
.mg-btn-cancel:hover { background:rgba(255,255,255,0.1); color:#fff; }

/* ── Game rows ── */
.mg-list { display:flex; flex-direction:column; gap:8px; }
.mg-row {
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.08);
  border-radius:10px; padding:14px 16px;
  display:flex; align-items:center; gap:12px;
  transition:border-color 0.15s;
}
.mg-row:hover { border-color:rgba(255,255,255,0.14); }
.mg-cover {
  width:56px; height:38px; border-radius:6px; object-fit:cover;
  background:rgba(255,255,255,0.06); display:flex; align-items:center;
  justify-content:center; font-size:18px; overflow:hidden; flex-shrink:0;
}
.mg-cover img { width:100%; height:100%; object-fit:cover; display:block; }
.mg-info { flex:1; min-width:0; }
.mg-actions { display:flex; gap:6px; flex-shrink:0; }
.mg-action-btn {
  padding:6px 10px; border-radius:6px; font-size:11px; font-weight:600;
  cursor:pointer; transition:all 0.12s; border:1px solid;
  background:none; font-family:'Inter',sans-serif; flex-shrink:0;
  white-space:nowrap;
}

.skel { background:#222; border-radius:8px; animation:skPulse 1.5s ease infinite; }
@keyframes skPulse { 0%,100%{opacity:.5} 50%{opacity:1} }

/* ════════════════════
   MOBILE ≤ 768px
════════════════════ */
@media(max-width:768px) {
  .mg-form { padding:16px; }
  .mg-form-grid  { grid-template-columns:1fr; }
  .mg-form-grid3 { grid-template-columns:1fr 1fr; }
  .mg-row { padding:12px 14px; gap:10px; }
  .mg-cover { width:48px; height:32px; }
  .mg-action-btn { padding:5px 8px; font-size:10px; }
}
@media(max-width:480px) {
  .mg-form-grid3 { grid-template-columns:1fr; }
  .mg-price-row  { grid-template-columns:1fr 1fr; }
}
`;

function GameForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(
    initial || { title:"", description:"", genre:[], price:"", active:true, featured:false, rating:"" }
  );
  const [imageFile, setImageFile] = useState(null);
  const [saving,    setSaving]    = useState(false);

  function toggleGenre(g) {
    setForm(f => ({
      ...f,
      genre: f.genre.includes(g) ? f.genre.filter(x => x !== g) : [...f.genre, g]
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title || !form.price) return;
    setSaving(true);
    try {
      const data = { ...form, price:parseFloat(form.price), rating:form.rating ? parseFloat(form.rating) : null };
      if (initial?.id) await updateGame(initial.id, data, imageFile);
      else await addGame(data, imageFile);
      onSave();
    } catch (err) { alert("Save failed: " + err.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="mg-form">
      <div className="mg-form-title">{initial?.id ? "✏️ Edit Game" : "➕ Add New Game"}</div>
      <form onSubmit={handleSubmit}>

        <div className="mg-form-grid">
          <div>
            <label className="mg-lbl">Game Title *</label>
            <input className="mg-inp" value={form.title}
              onChange={e => setForm(f => ({...f, title:e.target.value}))}
              placeholder="Cyberpunk 2077" required />
          </div>
          <div className="mg-price-row">
            <div>
              <label className="mg-lbl">Price (USD) *</label>
              <input className="mg-inp" type="number" step="0.01" min="0.99"
                value={form.price}
                onChange={e => setForm(f => ({...f, price:e.target.value}))}
                placeholder="4.99" required />
            </div>
            <div>
              <label className="mg-lbl">Rating (0–5)</label>
              <input className="mg-inp" type="number" step="0.1" min="0" max="5"
                value={form.rating}
                onChange={e => setForm(f => ({...f, rating:e.target.value}))}
                placeholder="4.5" />
            </div>
          </div>
        </div>

        <div style={{ marginBottom:14 }}>
          <label className="mg-lbl">Description</label>
          <textarea className="mg-inp" style={{ minHeight:70, resize:"vertical" }}
            value={form.description}
            onChange={e => setForm(f => ({...f, description:e.target.value}))}
            placeholder="Game description…" />
        </div>

        <div style={{ marginBottom:14 }}>
          <label className="mg-lbl">Genres</label>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:8 }}>
            {GENRES.map(g => (
              <button key={g} type="button" className="mg-genre-btn"
                onClick={() => toggleGenre(g)}
                style={{
                  background:  form.genre.includes(g) ? "rgba(0,120,242,0.12)" : "rgba(255,255,255,0.04)",
                  borderColor: form.genre.includes(g) ? "rgba(0,120,242,0.4)"  : "rgba(255,255,255,0.08)",
                  color:       form.genre.includes(g) ? "#0078F2"               : "#6b6b6b",
                }}>{g}
              </button>
            ))}
          </div>
        </div>

        <div className="mg-form-grid3">
          <div>
            <label className="mg-lbl">Cover Image</label>
            <input type="file" accept="image/*"
              onChange={e => setImageFile(e.target.files[0])}
              style={{ fontSize:12, color:"#6b6b6b", width:"100%", marginTop:4 }} />
          </div>
          <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", paddingTop:20 }}>
            <input type="checkbox" checked={form.active}
              onChange={e => setForm(f => ({...f, active:e.target.checked}))} />
            <span style={{ fontSize:12, color:"#a3a3a3" }}>Active (visible)</span>
          </label>
          <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", paddingTop:20 }}>
            <input type="checkbox" checked={form.featured}
              onChange={e => setForm(f => ({...f, featured:e.target.checked}))} />
            <span style={{ fontSize:12, color:"#a3a3a3" }}>Featured on Home</span>
          </label>
        </div>

        <div style={{ display:"flex", gap:10 }}>
          <button type="submit" className="mg-btn-save" disabled={saving}>
            {saving ? "Saving…" : initial?.id ? "Save Changes" : "Add Game"}
          </button>
          <button type="button" className="mg-btn-cancel" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

export default function ManageGames() {
  const [games,    setGames]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editGame, setEditGame] = useState(null);

  const load = () => getAllGames().then(g => { setGames(g); setLoading(false); });
  useEffect(() => { load(); }, []);

  async function handleDeactivate(id) {
    if (!confirm("Deactivate this game? It will be hidden from the store.")) return;
    await deactivateGame(id); load();
  }

  return (
    <>
      <style>{S}</style>

      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, gap:10 }}>
        <div style={{ fontSize:16, fontWeight:800, color:"#fff" }}>🎮 Manage Games</div>
        <button
          onClick={() => { setEditGame(null); setShowForm(true); }}
          style={{
            padding:"9px 16px", background:"#0078F2", border:"none", borderRadius:6,
            color:"#fff", fontFamily:"'Inter',sans-serif", fontSize:13, fontWeight:700,
            cursor:"pointer", flexShrink:0,
          }}
        >+ Add Game</button>
      </div>

      {(showForm || editGame) && (
        <GameForm
          initial={editGame}
          onSave={() => { setShowForm(false); setEditGame(null); load(); }}
          onCancel={() => { setShowForm(false); setEditGame(null); }}
        />
      )}

      <div style={{ fontSize:12, color:"#6b6b6b", marginBottom:12, fontWeight:500 }}>
        {games.length} games total
      </div>

      <div className="mg-list">
        {loading ? (
          [...Array(4)].map((_,i) => <div key={i} className="skel" style={{ height:68 }} />)
        ) : games.map(game => (
          <div key={game.id} className="mg-row" style={{ opacity:game.active ? 1 : 0.5 }}>
            <div className="mg-cover">
              {game.coverImage ? <img src={game.coverImage} alt={game.title} /> : "🎮"}
            </div>
            <div className="mg-info">
              <div style={{ fontSize:13, fontWeight:700, color:"#fff", marginBottom:3 }}>{game.title}</div>
              <div style={{ fontSize:11, color:"#6b6b6b" }}>
                ${game.price?.toFixed(2)} · {game.stock} left
                {!game.active  && " · 🔴 Hidden"}
                {game.featured && " · ⭐ Featured"}
              </div>
            </div>
            <div className="mg-actions">
              <button className="mg-action-btn"
                style={{ color:"#0078F2", borderColor:"rgba(0,120,242,0.2)" }}
                onClick={() => { setEditGame(game); setShowForm(false); }}>
                ✏️ Edit
              </button>
              {game.active && (
                <button className="mg-action-btn"
                  style={{ color:"#ef4444", borderColor:"rgba(239,68,68,0.2)" }}
                  onClick={() => handleDeactivate(game.id)}>
                  🗑️
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}